import React from "react";
import {
  Select,
  MenuItem,
  TextField,
  IconButton,
  Box,
  Chip,
  Button,
  Badge,
  Autocomplete,
} from "@mui/material";
import {
  DatePicker,
  LocalizationProvider,
  TimePicker,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import DeleteIcon from "@mui/icons-material/Delete";
import ChecklistIcon from "@mui/icons-material/Checklist";
import { generateTaskTypes } from "../utils/dispatchUtils";
import { getChecklistSummary } from "../utils/checklistUtils";

// Start/End times are stored as dayjs/Date objects; the default column
// comparator sorts them as strings ("Sun…" vs "Mon…"), which isn't
// chronological. Compare by timestamp instead.
const toMs = (value) => {
  if (!value) return 0;
  const d = dayjs(value);
  return d.isValid() ? d.valueOf() : 0;
};
const timeSortComparator = (a, b) => toMs(a) - toMs(b);

/**
 * Creates DataGrid columns for dispatch management
 * @param {Function} onCellEdit - Handler for cell edits
 * @param {Function} onDeleteRow - Handler for row deletion
 * @param {Array} employeeList - List of employees
 * @param {Array} roomsList - List of rooms
 * @param {string} selectedEmployeeId - Currently selected employee ID
 * @param {Function} onOpenChecklistModal - Handler for opening checklist modal
 * @returns {Array} - Array of column definitions
 */
export const createDispatchColumns = (
  onCellEdit,
  onDeleteRow,
  employeeList = [],
  roomsList = [],
  selectedEmployeeId = null,
  onOpenChecklistModal = null
) => {
  const selectedEmployee = employeeList.find(
    (emp) => emp.id === selectedEmployeeId
  );
  const taskTypes = generateTaskTypes(roomsList, selectedEmployee?.type);

  return [
    {
      field: "startTime",
      headerName: "Start Time",
      width: 180,
      sortComparator: timeSortComparator,
      renderCell: (params) => {
        const parsed = params.row.startTime
          ? dayjs(params.row.startTime)
          : null;

        return (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <TimePicker
              value={parsed}
              onAccept={(val) => onCellEdit(params.id, "startTime", val)}
              slotProps={{
                textField: {
                  size: "small",
                  fullWidth: true,
                },
              }}
            />
          </LocalizationProvider>
        );
      },
    },
    {
      field: "endTime",
      headerName: "End Time",
      width: 180,
      sortComparator: timeSortComparator,
      renderCell: (params) => {
        const parsed = params.row.endTime ? dayjs(params.row.endTime) : null;

        return (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <TimePicker
              value={parsed}
              onAccept={(val) => onCellEdit(params.id, "endTime", val)}
              slotProps={{
                textField: {
                  size: "small",
                  fullWidth: true,
                },
              }}
            />
          </LocalizationProvider>
        );
      },
    },
    {
      field: "task",
      headerName: "Task",
      width: 150,
      renderCell: (params) => {
        const allowedTasks = taskTypes.filter((t) =>
          t.taskType.includes(selectedEmployee?.type)
        );

        return (
          <Select
            fullWidth
            size="small"
            value={params.row.task || ""}
            onChange={(e) => onCellEdit(params.id, "task", e.target.value)}
            displayEmpty
          >
            <MenuItem value="">
              <em>Select Task</em>
            </MenuItem>
            {allowedTasks.map((task) => (
              <MenuItem key={task.task} value={task.task}>
                {task.task}
              </MenuItem>
            ))}
          </Select>
        );
      },
    },
    {
      field: "location",
      headerName: "Location",
      width: 220,
      renderCell: (params) => {
        const taskObj = taskTypes.find((t) => t.task === params.row.task);

        if (taskObj?.task === "Other") {
          return (
            <TextField
              fullWidth
              size="small"
              placeholder="Enter location"
              value={params.row.location || ""}
              onChange={(e) =>
                onCellEdit(params.id, "location", e.target.value)
              }
              onKeyDown={(e) => {
                // Prevent DataGrid from capturing space and other keys
                e.stopPropagation();
              }}
              onClick={(e) => {
                // Prevent DataGrid row selection when clicking in text field
                e.stopPropagation();
              }}
            />
          );
        }

        // Use Autocomplete for Room Turn tasks (searchable dropdown)
        if (taskObj?.task === "Room Turn") {
          return (
            <Autocomplete
              fullWidth
              size="small"
              options={taskObj?.taskLocations || []}
              value={params.row.location || null}
              onChange={(event, newValue) => {
                onCellEdit(params.id, "location", newValue || "");
              }}
              renderInput={(renderParams) => (
                <TextField
                  {...renderParams}
                  placeholder="Select Room"
                  onKeyDown={(e) => {
                    // Prevent DataGrid from capturing space and other keys
                    e.stopPropagation();
                  }}
                />
              )}
              disableClearable={false}
              onOpen={(e) => {
                // Prevent DataGrid row selection when opening dropdown
                e.stopPropagation();
              }}
            />
          );
        }

        // Regular Select for other tasks
        return (
          <Select
            fullWidth
            size="small"
            value={params.row.location || ""}
            onChange={(e) => onCellEdit(params.id, "location", e.target.value)}
            displayEmpty
          >
            <MenuItem value="">
              <em>Select Location</em>
            </MenuItem>
            {taskObj?.taskLocations?.map((loc) => (
              <MenuItem key={loc} value={loc}>
                {loc}
              </MenuItem>
            ))}
          </Select>
        );
      },
    },
    {
      field: "notes",
      headerName: "Notes",
      width: 200,
      renderCell: (params) => (
        <TextField
          fullWidth
          size="small"
          placeholder="Enter notes"
          value={params.row.notes || ""}
          onChange={(e) => onCellEdit(params.id, "notes", e.target.value)}
          onKeyDown={(e) => {
            // Prevent DataGrid from capturing space and other keys
            e.stopPropagation();
          }}
          onClick={(e) => {
            // Prevent DataGrid row selection when clicking in text field
            e.stopPropagation();
          }}
          multiline
          maxRows={2}
        />
      ),
    },
    {
      field: "checklists",
      headerName: "Checklists",
      width: 150,
      sortable: false,
      renderCell: (params) => {
        const checklists = params.row.checklists || [];
        const summary = getChecklistSummary(checklists);
        const badgeColor =
          summary.totalItems === 0
            ? "default"
            : summary.isComplete
            ? "success"
            : "primary";

        return (
          <Button
            variant="outlined"
            size="small"
            startIcon={<ChecklistIcon />}
            onClick={(e) => {
              e.stopPropagation();
              if (onOpenChecklistModal) {
                onOpenChecklistModal(params.id, checklists);
              }
            }}
            sx={{ minWidth: 120 }}
          >
            <Badge
              badgeContent={
                summary.totalItems > 0
                  ? `${summary.completedItems}/${summary.totalItems}`
                  : "0"
              }
              color={badgeColor}
            >
              Checklists
            </Badge>
          </Button>
        );
      },
    },
    {
      field: "status",
      headerName: "Status",
      width: 120,
      renderCell: (params) => {
        const getStatusColor = (status) => {
          switch (status) {
            case "completed":
              return "success";
            case "in_progress":
              return "warning";
            case "cancelled":
              return "error";
            default:
              return "default";
          }
        };

        return (
          <Chip
            label={params.row.status || "pending"}
            color={getStatusColor(params.row.status)}
            size="small"
            variant="outlined"
          />
        );
      },
    },
    {
      field: "duration",
      headerName: "Duration",
      width: 100,
      renderCell: (params) => {
        if (!params.row.startTime || !params.row.endTime) return "—";

        const start = dayjs(params.row.startTime);
        const end = dayjs(params.row.endTime);
        const duration = end.diff(start, "hours", true);

        if (duration <= 0) return "—";

        const hours = Math.floor(duration);
        const minutes = Math.round((duration - hours) * 60);

        return (
          <Box sx={{ fontSize: "0.875rem", color: "text.secondary" }}>
            {hours > 0 && `${hours}h `}
            {minutes > 0 && `${minutes}m`}
          </Box>
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 100,
      sortable: false,
      renderCell: (params) => (
        <IconButton
          size="small"
          onClick={() => onDeleteRow(params.id)}
          color="error"
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];
};
